import React, { useState, useRef, useEffect } from "react";
import mqtt from "mqtt";
import axios from "axios";

const matchTopic = (filter, topic) => {
  if (filter === "#") return true;
  const filterParts = filter.split("/");
  const topicParts = topic.split("/");
  for (let i = 0; i < filterParts.length; i++) {
    if (filterParts[i] === "#") return true;
    if (filterParts[i] === "+") continue;
    if (!topicParts[i] || filterParts[i] !== topicParts[i]) return false;
  }
  return filterParts.length === topicParts.length;
};

function App() {
  const [client, setClient] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  const [brokerUrl, setBrokerUrl] = useState("ws://smartifyindia.co.in:9001");

  const [pubTopic, setPubTopic] = useState("");
  const pubTopicRef = useRef("");
  useEffect(() => {
    pubTopicRef.current = pubTopic;
  }, [pubTopic]);

  const [pubMessage, setPubMessage] = useState("");
  const [subTopic, setSubTopic] = useState("");
  const [subTopics, setSubTopics] = useState([]);
  const subTopicsRef = useRef([]);

  const [deviceMessages, setDeviceMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);

  const [allMsgSearch, setAllMsgSearch] = useState("");
  const [filteredMsgSearch, setFilteredMsgSearch] = useState("");

  const presetMessages = ["allon", "alloff", "s05", "updatecode", "getallwifi"];
  const debugMessages = ["Debugon", "Debugoff"];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ssid, setSsid] = useState("");
  const [password, setPassword] = useState("");

  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [wifiList, setWifiList] = useState([]);
  const [isFetchingWifi, setIsFetchingWifi] = useState(false);
  const isFetchingWifiRef = useRef(false);
  useEffect(() => {
    isFetchingWifiRef.current = isFetchingWifi;
  }, [isFetchingWifi]);

  const [deviceDetails, setDeviceDetails] = useState(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  const connectButtonRef = useRef(null);

  let savedDevices = [];
  const savedDevicesRaw = localStorage.getItem("subscribedDevices");
  if (savedDevicesRaw) {
    savedDevices = JSON.parse(savedDevicesRaw).map(d => ({
      ...d,
      UniqueNumber: d.UniqueNumber.toString()
    }));
  }

  const [subscribedDevices, setSubscribedDevices] = useState(savedDevices);
  const subscribedDevicesRef = useRef(savedDevices);

  useEffect(() => {
    if (!isConnected || !client) return;
    subscribedDevices.forEach(dev => {
      ["client", "device"].forEach(suffix => {
        const topic = `${dev.UniqueNumber}/${suffix}`;
        client.subscribe(topic, (err) => {
          if (!err)
            addMessage(`📡 Auto-subscribed to ${topic}`, setFilteredMessages);
        });
      });
    });
  }, [isConnected, client, subscribedDevices]);

  useEffect(() => {
    if (!isConnected) {
      connectBroker();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }}, []);

  useEffect(() => {
    if (connectButtonRef.current) {
      connectButtonRef.current.focus();
  }}, [isConnected]);

  useEffect(() => {
    if (!pubTopic) {
      setDeviceDetails(null);
      return;
    }
    const timerId = setTimeout(() => {
      setIsFetchingDetails(true);
      setDeviceDetails(null);
      axios
        .post("http://smartifyindia.co.in:8080/api/GetDeviceDetailbyUniqueNumber", {
          UniqueNumber: pubTopic,
        })
        .then((res) => {
          if (res.data && res.data.length > 0) {
            setDeviceDetails(res.data[0]);
          } else {
            setDeviceDetails({ error: "Device not found" });
          }
        })
        .catch((err) => {
          console.error("Error fetching device details:", err);
          setDeviceDetails({ error: "Error fetching details" });
        })
        .finally(() => {
          setIsFetchingDetails(false);
        });
    }, 500);
    return () => clearTimeout(timerId);
  }, [pubTopic]);

  const addMessage = (msg, setter) => {
    const timestamp = new Date().toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour12: false,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit'
    });
    setter((prev) => [{ text: msg, timestamp }, ...prev]);
  };

  const connectBroker = () => {
    const mqttClient = mqtt.connect(brokerUrl);

    mqttClient.on("connect", () => {
      console.log("✅ Connected:", brokerUrl);
      setIsConnected(true);
      mqttClient.subscribe("#", (err) => {
        if (!err)
          addMessage("📡 Auto-subscribed to # (all devices)", setDeviceMessages);
      });

      subscribedDevicesRef.current.forEach((dev) => {
        ["client", "device"].forEach((suffix) => {
          const topic = `${dev.UniqueNumber}/${suffix}`;
          mqttClient.subscribe(topic, (err) => {
            if (!err)
              addMessage(`📡 Auto-subscribed to ${topic}`, setFilteredMessages);
          });
        });
      });
    });

    mqttClient.on("message", (topic, msg) => {
      const messageString = msg.toString();
      const fullMessageLog = `${topic}: ${messageString}`;
      addMessage(fullMessageLog, setDeviceMessages);

      if (
        isFetchingWifiRef.current &&
        topic === `${pubTopicRef.current}/client`
      ) {
        const entries = messageString.split(';');
        const parsedWifis = entries.map(entry => {
          const parts = entry.split(',');
          if (parts.length === 2) {
            const ssid = parts[0].trim();
            const pass = parts[1].trim();
            if (ssid) {
              return { ssid, pass };
            }
          }
          return null;
        }).filter(w => w);

        setWifiList(parsedWifis);
        setIsFetchingWifi(false);
      }

      if (
        subTopicsRef.current.some((t) => matchTopic(t, topic)) ||
        subscribedDevicesRef.current.some(
          (d) =>
            matchTopic(`${d.UniqueNumber}/client`, topic) ||
            matchTopic(`${d.UniqueNumber}/device`, topic)
        )
      ) {
        addMessage(fullMessageLog, setFilteredMessages);
      }
    });

    mqttClient.on("error", (err) =>
      console.error("❌ MQTT Error:", err.message)
    );

    setClient(mqttClient);
  };

  const disconnectBroker = () => {
    if (client) {
      client.end();
      setIsConnected(false);
      addMessage("🔌 Disconnected", setDeviceMessages);
    }
  };

  const publishMessage = () => {
    if (client && pubTopic && pubMessage) {
      let topicToUse = pubTopic.trim();
      if (!topicToUse.includes('/')) {
        topicToUse = `${topicToUse}/device`;
      }
      client.publish(topicToUse, pubMessage);
      addMessage(
        `➡️ Published: "${pubMessage}" to ${topicToUse}`,
        setDeviceMessages
      );
      setPubMessage("");
    }
  };

  const publishPreset = (message) => {
    if (client && pubTopic) {
      let topicToUse = pubTopic.trim();
      if (!topicToUse.includes('/')) {
        topicToUse = `${topicToUse}/device`;
      }
      client.publish(topicToUse, message);
      addMessage(
        `➡️ Published Preset: "${message}" to ${topicToUse}`,
        setDeviceMessages
      );
    }
  };

  const handleAddWifi = () => {
    if (client && pubTopic && ssid) {
      const message = `ADDWIFIssid=${ssid}pass=${password}`;
      let topicToUse = pubTopic.trim();
      if (!topicToUse.includes('/')) {
        topicToUse = `${topicToUse}/device`;
      }
      client.publish(topicToUse, message);
      addMessage(
        `➡️ Published: "${message}" to ${topicToUse}`,
        setDeviceMessages
      );
      setSsid("");
      setPassword("");
      setIsModalOpen(false);
    } else {
      alert("Please enter a UniqueNumber and SSID.");
    }
  };

  const openRemoveModal = () => {
    if (!pubTopic) {
      alert("Please enter a UniqueNumber first.");
      return;
    }
    setIsFetchingWifi(true);
    setWifiList([]);
    setIsRemoveModalOpen(true);
    publishPreset("getallwifi");
  };

  const closeRemoveModal = () => {
    setIsRemoveModalOpen(false);
    setWifiList([]);
    setIsFetchingWifi(false);
  };

  const handleRemoveWifi = (wifiToRemove) => {
    if (client && pubTopic) {
      const message = `REMOVEWIFIssid=${wifiToRemove.ssid}pass=${wifiToRemove.pass}`;
      let topicToUse = pubTopic.trim();
      if (!topicToUse.includes('/')) {
        topicToUse = `${topicToUse}/device`;
      }
      client.publish(topicToUse, message);
      addMessage(
        `➡️ Published: "${message}" to ${topicToUse}`,
        setDeviceMessages
      );
      setWifiList(currentList => currentList.filter(w => w.ssid !== wifiToRemove.ssid));
    }
  };

  const subscribeTopic = () => {
    if (!client || !subTopic) return;
    const topicList = !subTopic.includes('/')
      ? [`${subTopic}/client`, `${subTopic}/device`]
      : [subTopic];

    topicList.forEach(topic => {
      client.subscribe(topic, (err) => {
        if (!err) addMessage(`📡 Subscribed to ${topic}`, setFilteredMessages);
      });
    });

    const uniqueNumberStr = subTopic.toString();
    if (!subscribedDevices.some(d => d.UniqueNumber === uniqueNumberStr) && /^[0-9]+$/.test(uniqueNumberStr)) {
      const newDevice = { UniqueNumber: uniqueNumberStr, DeviceName: `Device ${uniqueNumberStr}` };
      const updatedDevices = [...subscribedDevices, newDevice];
      setSubscribedDevices(updatedDevices);
      localStorage.setItem("subscribedDevices", JSON.stringify(updatedDevices));
    }
    setSubTopic("");
  };

  const unsubscribeTopic = (topic) => {
    if (client) {
      client.unsubscribe(topic, (err) => {
        if (!err) {
          addMessage(`🚫 Unsubscribed from ${topic}`, setFilteredMessages);
          setSubTopics((prev) => {
            const updated = prev.filter((t) => t !== topic);
            subTopicsRef.current = updated;
            return updated;
          });
        }
      });
    }
  };

  const subscribeDeviceFromList = (device) => {
    if (!client || !device) return;
    const uniqueNumberStr = device.UniqueNumber.toString();
    if (subscribedDevices.some(d => d.UniqueNumber === uniqueNumberStr)) return;
    ["client", "device"].forEach(suffix => {
      client.subscribe(`${uniqueNumberStr}/${suffix}`, err => {
        if (!err) addMessage(`📡 Subscribed to ${uniqueNumberStr}/${suffix}`, setFilteredMessages);
      });
    });
    const newDevice = { ...device, UniqueNumber: uniqueNumberStr };
    const updatedDevices = [...subscribedDevices, newDevice];
    setSubscribedDevices(updatedDevices);
    localStorage.setItem("subscribedDevices", JSON.stringify(updatedDevices));
  };

  const removeDeviceFromList = (device) => {
    if (!client || !device) return;
    const uniqueNumberStr = device.UniqueNumber.toString();
    ["client", "device"].forEach(suffix => {
      client.unsubscribe(`${uniqueNumberStr}/${suffix}`, err => {
        if (!err) addMessage(`🚫 Unsubscribed from ${uniqueNumberStr}/${suffix}`, setFilteredMessages);
      });
    });
    const updatedDevices = subscribedDevices.filter(d => d.UniqueNumber !== uniqueNumberStr);
    setSubscribedDevices(updatedDevices);
    localStorage.setItem("subscribedDevices", JSON.stringify(updatedDevices));
  };

  const clearAllMessages = () => setDeviceMessages([]);
  const clearFilteredMessages = () => setFilteredMessages([]);

  useEffect(() => {
    axios
      .post("http://smartifyindia.co.in:8080/api/GetAllLocationsNameList")
      .then((res) => setLocations(res.data))
      .catch((err) => console.error("Error fetching locations:", err));
  }, []);

  useEffect(() => {
    if (!selectedLocation) return;
    axios
      .post("http://smartifyindia.co.in:8080/api/GetRoomsbyLocationId", {
        LocationID: selectedLocation.LocationID,
      })
      .then((res) => setRooms(res.data))
      .catch((err) => console.error("Error fetching rooms:", err));
    setSelectedRoom(null);
    setDevices([]);
    setSelectedDevice(null);
  }, [selectedLocation]);

  useEffect(() => {
    if (!selectedRoom) return;
    axios
      .post("http://smartifyindia.co.in:8080/api/GetDevicesbyRoomId", {
        RoomID: selectedRoom.RoomID,
      })
      .then((res) => setDevices(res.data))
      .catch((err) => console.error("Error fetching devices:", err));
    setSelectedDevice(null);
  }, [selectedRoom]);

  const filteredAllMessages = deviceMessages.filter((msg) =>
    msg.text.toLowerCase().includes(allMsgSearch.toLowerCase())
  );

  const filteredFilteredMessages = filteredMessages.filter((msg) =>
    msg.text.toLowerCase().includes(filteredMsgSearch.toLowerCase())
  );

  return (
    <div className="font-sans p-1 sm:p-2 h-screen overflow-hidden flex flex-col">

      {/* App Header - Compact */}
      <div className="flex items-center justify-between mb-1 flex-wrap gap-1 flex-shrink-0">
        <h1 className="text-sm sm:text-base m-0 font-bold">Smartify Message Box</h1>
        <div className="flex items-center gap-1">
          {!isConnected && (
            <>
              <input
                type="text"
                value={brokerUrl}
                onChange={(e) => setBrokerUrl(e.target.value)}
                className="p-1 w-40 sm:w-56 text-xs border border-[#ccc] rounded"
              />
              <button ref={connectButtonRef} onClick={connectBroker} className="bg-[#007bff] text-white px-2 py-1 rounded border-none cursor-pointer hover:bg-[#0056b3] text-xs whitespace-nowrap">Connect</button>
            </>
          )}
          {isConnected && (
            <button onClick={disconnectBroker} className="bg-[#f66] text-white px-2 py-1 rounded border-none cursor-pointer text-xs">
              Disconnect
            </button>
          )}
        </div>
      </div>

      {/* Main Grid - full height, 3 columns on md+, 1 on mobile */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-1 sm:gap-2 flex-1 min-h-0">

        {/* Column 1: All Device Messages */}
        <div className="border border-[#ccc] p-2 rounded bg-[#f9f9f9] flex flex-col min-h-0">
          <h3 className="text-xs font-bold mb-1 flex-shrink-0">All Messages</h3>
          <div className="flex items-center gap-1 mb-1 flex-shrink-0">
            <input
              type="text"
              placeholder="Search..."
              value={allMsgSearch}
              onChange={(e) => setAllMsgSearch(e.target.value)}
              className="flex-1 p-1 text-xs border border-[#ccc] rounded"
            />
            <button onClick={clearAllMessages} className="bg-[#6c757d] text-white border-none px-2 py-1 rounded cursor-pointer hover:bg-[#5a6268] text-xs whitespace-nowrap">Clear</button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto bg-white border border-[#ddd] rounded font-mono">
            {filteredAllMessages.length === 0 && <p className="text-xs text-[#999] p-1">No messages</p>}
            {filteredAllMessages.map((msg, i) => (
              <div key={i} className="text-[10px] sm:text-xs leading-tight px-1 py-0.5 border-b border-[#f5f5f5]">
                <span className="text-[#888] mr-1">{msg.timestamp}</span>{msg.text}
              </div>
            ))}
          </div>
        </div>

        {/* Column 2: Controls */}
        <div className="border border-[#ccc] p-2 rounded bg-[#f9f9f9] flex flex-col gap-1 overflow-y-auto">

          {/* Publish Section */}
          <div className="flex-shrink-0">
            <div className="flex items-center gap-1 mb-1">
              <span className="text-xs font-bold w-14">Publish:</span>
              <input
                type="text"
                value={pubTopic}
                onChange={(e) => setPubTopic(e.target.value)}
                placeholder="ID"
                className="p-0.5 px-1 text-xs border border-[#ccc] rounded w-24"
              />
              <input
                type="text"
                value={pubMessage}
                onChange={(e) => setPubMessage(e.target.value)}
                placeholder="Msg"
                className="flex-1 p-0.5 px-1 text-xs border border-[#ccc] rounded"
              />
              <button onClick={publishMessage} className="bg-[#007bff] text-white px-1 py-0.5 rounded border-none cursor-pointer hover:bg-[#0056b3] text-[10px] whitespace-nowrap">Send</button>
            </div>
            <div className="text-[9px] sm:text-[10px] text-[#555] px-1 mb-1 leading-tight">
              {isFetchingDetails && <span className="text-[#999]">Loading...</span>}
              {deviceDetails && !deviceDetails.error && (
                <span>{deviceDetails.DeviceMasterUser} - {deviceDetails.LocationName} / {deviceDetails.RoomName} / {deviceDetails.DeviceType}</span>
              )}
              {deviceDetails && deviceDetails.error && (
                <span className="text-[#dc3545]">{deviceDetails.error}</span>
              )}
            </div>
          </div>

          {/* Preset + Debug Buttons */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-1 flex-shrink-0">
            {presetMessages.map(msg => (
              <button key={msg} onClick={() => publishPreset(msg)} className="bg-[#007bff] text-white rounded border-none cursor-pointer hover:bg-[#0056b3] text-[9px] sm:text-[10px] py-0.5 px-0.5 leading-tight">
                {msg}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-1 flex-shrink-0">
            {debugMessages.map(msg => (
              <button key={msg} onClick={() => publishPreset(msg)} className="bg-[#28a745] text-white rounded border-none cursor-pointer hover:bg-[#1e7e34] text-[9px] sm:text-[10px] py-0.5 px-0.5 leading-tight">
                {msg}
              </button>
            ))}
          </div>

          {/* Wifi Buttons */}
          <div className="flex gap-1 flex-shrink-0">
            <button className="flex-1 bg-[#007bff] text-white text-[10px] border-none hover:bg-[#0056b3] py-0.5 px-1 rounded" onClick={() => setIsModalOpen(true)}>Add Wifi</button>
            <button className="flex-1 bg-[#dc3545] text-white text-[10px] border-none hover:bg-[#c82333] py-0.5 px-1 rounded" onClick={openRemoveModal}>Remove Wifi</button>
          </div>

          {/* Manual Subscribe */}
          <div className="flex gap-1 items-center flex-shrink-0">
            <input
              type="text"
              value={subTopic}
              onChange={(e) => setSubTopic(e.target.value)}
              placeholder="Topic/ID"
              className="flex-1 p-0.5 text-xs border border-[#ccc] rounded"
            />
            <button onClick={subscribeTopic} className="bg-[#007bff] text-white px-1 py-0.5 rounded border-none cursor-pointer text-[10px] whitespace-nowrap">Sub</button>
          </div>
          {subTopics.length > 0 && (
            <div className="flex flex-wrap gap-1 flex-shrink-0">
              {subTopics.map((t, i) => (
                <span key={i} className="text-[10px] bg-[#e9ecef] px-1 py-0.5 rounded flex items-center gap-0.5">
                  {t}
                  <button onClick={() => unsubscribeTopic(t)} className="bg-[#fbe0e2] text-[#dc3545] border-none rounded px-0.5 py-0 cursor-pointer text-[9px]">×</button>
                </span>
              ))}
            </div>
          )}

          {/* Device Selector - all on ONE line */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <select
              value={selectedLocation?.LocationID || ""}
              onChange={(e) =>
                setSelectedLocation(locations.find((l) => l.LocationID === parseInt(e.target.value)))
              }
              className="p-0.5 text-[10px] border border-[#ccc] rounded flex-1 min-w-0"
            >
              <option value="">Loc</option>
              {locations.map((l) => (
                <option key={l.LocationID} value={l.LocationID}>{l.LocationWithName}</option>
              ))}
            </select>
            <select
              value={selectedRoom?.RoomID || ""}
              onChange={(e) =>
                setSelectedRoom(rooms.find((r) => r.RoomID === parseInt(e.target.value)))
              }
              className="p-0.5 text-[10px] border border-[#ccc] rounded flex-1 min-w-0"
            >
              <option value="">Room</option>
              {rooms.map((r) => (
                <option key={r.RoomID} value={r.RoomID}>{r.RoomName}</option>
              ))}
            </select>
            <select
              value={selectedDevice?.UniqueNumber || ""}
              onChange={(e) =>
                setSelectedDevice(devices.find((d) => d.UniqueNumber === e.target.value))
              }
              className="p-0.5 text-[10px] border border-[#ccc] rounded flex-1 min-w-0"
            >
              <option value="">Device</option>
              {devices.map((d) => (
                <option key={d.UniqueNumber} value={d.UniqueNumber}>{d.DeviceName}</option>
              ))}
            </select>
            <button
              onClick={() => selectedDevice && subscribeDeviceFromList(selectedDevice)}
              className="bg-[#007bff] text-white px-1 py-0.5 rounded border-none cursor-pointer text-[10px] whitespace-nowrap flex-shrink-0"
            >
              Sub
            </button>
          </div>

          {/* Subscribed Devices Table - single column */}
          {subscribedDevices.length > 0 && (
            <div className="flex-shrink-0">
              <span className="text-[10px] font-bold block mb-0.5">Subscribed ({subscribedDevices.length})</span>
              <div className="max-h-[100px] overflow-y-auto">
                <table className="w-full text-[10px] border-collapse">
                  <thead>
                    <tr className="bg-[#f2f2f2]">
                      <th className="text-left p-0.5 font-bold border-b border-[#eee]">Device</th>
                      <th className="text-center p-0.5 font-bold border-b border-[#eee] w-6">×</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribedDevices.map((d) => (
                      <tr key={d.UniqueNumber}>
                        <td className="p-0.5 border-b border-[#eee] truncate">{d.DeviceName || d.UniqueNumber}</td>
                        <td className="p-0.5 border-b border-[#eee] text-center">
                          <button onClick={() => removeDeviceFromList(d)} className="bg-[#fbe0e2] text-[#dc3545] border-none rounded px-0.5 py-0 cursor-pointer text-[10px]">×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Column 3: Filtered Messages */}
        <div className="border border-[#ccc] p-2 rounded bg-[#f9f9f9] flex flex-col min-h-0">
          <h3 className="text-xs font-bold mb-1 flex-shrink-0">Filtered</h3>
          <div className="flex items-center gap-1 mb-1 flex-shrink-0">
            <input
              type="text"
              placeholder="Search..."
              value={filteredMsgSearch}
              onChange={(e) => setFilteredMsgSearch(e.target.value)}
              className="flex-1 p-1 text-xs border border-[#ccc] rounded"
            />
            <button onClick={clearFilteredMessages} className="bg-[#6c757d] text-white border-none px-2 py-1 rounded cursor-pointer hover:bg-[#5a6268] text-xs whitespace-nowrap">Clear</button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto bg-white border border-[#ddd] rounded font-mono">
            {filteredFilteredMessages.length === 0 && <p className="text-xs text-[#999] p-1">No messages</p>}
            {filteredFilteredMessages.map((msg, i) => {
              const color = msg.text.includes("/client")
                ? "#cce5ff"
                : msg.text.includes("/device") || msg.text.includes("Published")
                ? "#d4edda"
                : "#fff";
              return (
                <div key={i} className="text-[10px] sm:text-xs leading-tight px-1 py-0.5 border-b border-[#f5f5f5]" style={{ background: color }}>
                  <span className="text-[#888] mr-1">{msg.timestamp}</span>{msg.text}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Add WiFi Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-[1000] p-4">
          <div className="bg-white p-4 rounded-lg w-full max-w-[360px] shadow-lg">
            <h2 className="text-sm font-bold mt-0 mb-2">Add Wi-Fi Network</h2>
            <p className="text-[10px] mb-2 text-[#666]">Enter the credentials for the device.</p>
            <input
              className="w-full p-2 mb-2 text-xs border border-[#ccc] rounded box-border"
              type="text"
              placeholder="SSID (Wi-Fi Name)"
              value={ssid}
              onChange={(e) => setSsid(e.target.value)}
            />
            <input
              className="w-full p-2 mb-3 text-xs border border-[#ccc] rounded box-border"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-2">
              <button className="bg-[#007bff] text-white px-3 py-1 rounded border-none cursor-pointer hover:bg-[#0056b3] text-xs" onClick={handleAddWifi}>Connect</button>
              <button className="bg-[#e9ecef] text-black px-3 py-1 rounded border-none cursor-pointer hover:bg-[#ced4da] text-xs" onClick={() => setIsModalOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Remove WiFi Modal */}
      {isRemoveModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-[1000] p-4">
          <div className="bg-white p-4 rounded-lg w-full max-w-[360px] shadow-lg">
            <h2 className="text-sm font-bold mt-0 mb-2">Remove Saved Wi-Fi</h2>
            {isFetchingWifi ? (
              <p className="text-xs text-[#666]">Loading networks from device...</p>
            ) : wifiList.length > 0 ? (
              <ul className="list-none p-0 m-0 max-h-[180px] overflow-y-auto">
                {wifiList.map((wifi, index) => (
                  <li key={`${wifi.ssid}-${index}`} className="flex justify-between items-center py-1 border-b border-[#eee] last:border-b-0 text-xs">
                    <span className="truncate flex-1 mr-2">{wifi.ssid}</span>
                    <button className="bg-[#fbe0e2] text-[#dc3545] border-none rounded-full w-5 h-5 cursor-pointer font-bold p-0 hover:bg-[#ffd6d9] text-xs flex-shrink-0" onClick={() => handleRemoveWifi(wifi)}>×</button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-[#666]">No Wi-Fi networks found on the device.</p>
            )}
            <div className="flex justify-end mt-3">
              <button className="bg-[#e9ecef] text-black px-3 py-1 rounded border-none cursor-pointer hover:bg-[#ced4da] text-xs" onClick={closeRemoveModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
